<div class="audit-shell">
  <div class="state-container" *ngIf="isLoadingOperation">
    <nz-spin nzSize="large"></nz-spin>
    <p>Loading operation details...</p>
  </div>

  <div class="state-container state-error" *ngIf="!isLoadingOperation && hasOperationError">
    <span nz-icon nzType="warning" nzTheme="outline"></span>
    <p>We could not load the audit details right now.</p>
    <button nz-button nzType="primary" (click)="retryOperationLoad()">
      <span nz-icon nzType="reload"></span>
      Retry Loading
    </button>
  </div>

  <ng-container *ngIf="!isLoadingOperation && !hasOperationError">
    <ng-container *ngIf="auditableSeats().length > 0; else emptySeats">
      <div class="audit-top-bar" *ngIf="activeSeat() as seat">
        <div class="top-bar-left">
          <button nz-button nzType="link" class="back-button" [routerLink]="['/audits/my-audits']">
            <span nz-icon nzType="arrow-left"></span>
          </button>

          <div class="route-heading">
            <h2>{{ operationData()?.routeName || 'Unknown Route' }}</h2>
          </div>
        </div>

        <div class="top-bar-center">
          <div class="seat-highlight">
            <button
              type="button"
              class="seat-nav-arrow"
              title="Previous seat (←)"
              [disabled]="auditableSeats().length <= 1"
              (click)="navigateSeat('previous')"
            >
              <span nz-icon nzType="left" nzTheme="outline"></span>
            </button>
            <span class="meta-pill meta-pill--icon" [ngClass]="getSeatClassBadge(seat.class)">
              <span nz-icon nzType="number" nzTheme="outline"></span>
              <span>Seat {{ seat.seatNumber }}</span>
            </span>
            <span class="seat-nickname" *ngIf="seat.nickName">{{ seat.nickName }}</span>
            <button
              type="button"
              class="seat-nav-arrow"
              title="Next seat (→)"
              [disabled]="auditableSeats().length <= 1"
              (click)="navigateSeat('next')"
            >
              <span nz-icon nzType="right" nzTheme="outline"></span>
            </button>
          </div>
        </div>

        <div class="top-bar-right">
          <div class="audit-progress" *ngIf="totalAuditableSeats() > 0">
            <div class="progress-track">
              <div class="progress-fill" [style.width.%]="seatProgressPercent()"></div>
            </div>
            <div class="progress-meta">{{ auditedSeatsCount() }} / {{ totalAuditableSeats() }} seats</div>
          </div>

          <div class="top-bar-actions">
            <button
              type="button"
              class="btn btn-sm btn-ghost btn-square"
              title="Pause and return to My Audits"
              (click)="pauseAudit()"
            >
              <span nz-icon nzType="pause" nzTheme="outline"></span>
            </button>
            <button
              type="button"
              class="btn btn-sm btn-ghost btn-square"
              nz-dropdown
              nzTrigger="click"
              [nzDropdownMenu]="auditOverflowMenu"
              title="More actions"
            >
              <span nz-icon nzType="more" nzTheme="outline"></span>
            </button>
            <nz-dropdown-menu #auditOverflowMenu="nzDropdownMenu">
              <ul nz-menu>
                <li nz-menu-item nzDanger (click)="cancelAudit()">
                  <span nz-icon nzType="close-circle" nzTheme="outline" style="margin-inline-end: 0.5rem"></span>
                  <span>Cancel auditing</span>
                </li>
              </ul>
            </nz-dropdown-menu>
          </div>
        </div>
      </div>

      <buspro-admin-audit-seat-passenger-stepper
        *ngIf="activeSeat()"
        [passengers]="activeSeatPassengers()"
        [activePassengerId]="activePassenger()?.passenger?.id ?? null"
        [isEmpty]="activeSeatIsEmpty()"
        [riderCount]="unbookedRidersForActiveSeat().length"
        (select)="selectPassenger($event)"
      />

      <div class="audit-layout" *ngIf="activeSeat() as seat">
        <aside class="audit-sidebar audit-sidebar--left">
          <header class="sidebar-header">
            <p class="sidebar-eyebrow">Seat Arrangement</p>
          </header>

          <div
            class="seat-grid"
            *ngIf="seats().length > 0; else seatsEmpty"
            [style.gridTemplateColumns]="seatGridColumns()"
          >
            <div
              *ngFor="let seat of seats(); trackBy: trackBySeat"
              [ngClass]="seatBlockClasses(seat)"
              class="seat-block"
              [title]="seatTooltip(seat)"
              [attr.role]="isSeatSelectable(seat) ? 'button' : null"
              [attr.tabindex]="isSeatSelectable(seat) ? 0 : null"
              [attr.aria-label]="isSeatSelectable(seat) ? seatTooltip(seat) : null"
              (click)="selectSeat(seat)"
              (keydown.enter)="selectSeat(seat)"
              (keydown.space)="$event.preventDefault(); selectSeat(seat)"
            >
              <span *ngIf="isSeatDone(seat)" class="seat-block__indicator">
                <span class="sr-only">Seat fully accounted for</span>
              </span>
              <span *ngIf="seatBlockLabel(seat) as label" class="seat-block__label">{{ label }}</span>
            </div>
          </div>

          <ng-template #seatsEmpty>
            <div class="sidebar-placeholder">
              <nz-empty nzNotFoundContent="No seats found"></nz-empty>
            </div>
          </ng-template>
        </aside>

        <section class="audit-main">
          <div class="bus-stop-track" *ngIf="busStops().length > 0">
            <div class="bus-stop-track__line"></div>
            <div class="bus-stop-tabs">
              <ng-container *ngFor="let stop of busStops(); trackBy: trackByStop; let idx = index">
                <button
                  type="button"
                  nz-dropdown
                  nzTrigger="click"
                  [nzDropdownMenu]="stopActionMenu"
                  class="bus-stop-tab"
                  [class.bus-stop-tab--active]="selectedBusStopId() === stop.id"
                  [class.bus-stop-tab--booked]="getBusStopVisualState(stop) === 'booked-range'"
                  [class.bus-stop-tab--beyond]="getBusStopVisualState(stop) === 'beyond-range'"
                  [class.bus-stop-tab--selected]="getBusStopVisualState(stop) === 'selected'"
                  [class.bus-stop-tab--start]="isAuditedStart(stop)"
                  [class.bus-stop-tab--end]="isAuditedEnd(stop)"
                  (click)="selectBusStop(stop.id)"
                >
                  <span class="stop-pill">{{ idx + 1 }}</span>
                  <div class="stop-meta">
                    <span class="stop-name-row">
                      <span
                        *ngIf="isBookedStart(stop)"
                        class="stop-flag stop-flag--start"
                        title="Booked boarding (from ticket)"
                        aria-label="Booked boarding"
                      >
                        <svg class="stop-flag__icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <path class="stop-flag__pole" d="M6 3.2v17.6" />
                          <path class="stop-flag__cloth" d="M6.8 3.8h10.4l-2.5 3.2 2.5 3.2H6.8z" />
                        </svg>
                      </span>
                      <span
                        *ngIf="isBookedEnd(stop)"
                        class="stop-flag stop-flag--end"
                        title="Booked drop-off (from ticket)"
                        aria-label="Booked drop-off"
                      >
                        <svg class="stop-flag__icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <path class="stop-flag__pole" d="M6 3.2v17.6" />
                          <rect class="stop-flag__cloth-outline" x="6.6" y="3.5" width="11" height="7.1" rx="0.6" />
                          <g class="stop-flag__check">
                            <rect x="6.6" y="3.5" width="3.67" height="2.37" />
                            <rect x="13.93" y="3.5" width="3.67" height="2.37" />
                            <rect x="10.26" y="5.87" width="3.67" height="2.37" />
                            <rect x="6.6" y="8.24" width="3.67" height="2.37" />
                            <rect x="13.93" y="8.24" width="3.67" height="2.37" />
                          </g>
                        </svg>
                      </span>
                      <span class="stop-name" [title]="stop.name">{{ stop.name }}</span>
                    </span>
                    <span class="stop-badges">
                      <span *ngIf="isAuditedStart(stop)" class="stop-badge stop-badge--start">START</span>
                      <span *ngIf="isAuditedEnd(stop)" class="stop-badge stop-badge--end">END</span>
                    </span>
                  </div>
                </button>
                <nz-dropdown-menu #stopActionMenu="nzDropdownMenu">
                  <ul nz-menu>
                    <li nz-menu-item [nzDisabled]="!canMarkAsStart(stop)" (click)="markStopAsStart(stop.id)">
                      <span nz-icon nzType="login" nzTheme="outline" style="margin-inline-end: 0.5rem"></span>
                      <span>Mark as Start</span>
                    </li>
                    <li nz-menu-item [nzDisabled]="!canMarkAsEnd(stop)" (click)="markStopAsEnd(stop.id)">
                      <span nz-icon nzType="logout" nzTheme="outline" style="margin-inline-end: 0.5rem"></span>
                      <span>Mark as End</span>
                    </li>
                  </ul>
                </nz-dropdown-menu>
              </ng-container>
            </div>
          </div>

          <div class="mdvr-gallery" *ngIf="selectedBusStopId(); else promptSelectStop">
            <header class="gallery-header">
              <div>
                <h3>{{ getSelectedBusStop()?.name }}</h3>
              </div>
              @if (entrySnapshots(selectedBusStopId()!).length > 0 || exitSnapshots(selectedBusStopId()!).length > 0) {
                <div class="flex items-center gap-2">
                  <div class="join" role="group" aria-label="Switch between entry and exit photos">
                    <button
                      type="button"
                      class="join-item btn btn-sm"
                      [class.btn-primary]="shownDirection() === 'entry'"
                      [disabled]="entrySnapshots(selectedBusStopId()!).length === 0"
                      (click)="setGalleryDirection('entry')"
                    >
                      <span nz-icon nzType="login" nzTheme="outline"></span>
                      Entry ({{ entrySnapshots(selectedBusStopId()!).length }})
                    </button>
                    <button
                      type="button"
                      class="join-item btn btn-sm"
                      [class.btn-primary]="shownDirection() === 'exit'"
                      [disabled]="exitSnapshots(selectedBusStopId()!).length === 0"
                      (click)="setGalleryDirection('exit')"
                    >
                      <span nz-icon nzType="logout" nzTheme="outline"></span>
                      Exit ({{ exitSnapshots(selectedBusStopId()!).length }})
                    </button>
                  </div>
                  <button
                    type="button"
                    class="btn btn-sm btn-square"
                    [class.btn-primary]="showAllTags()"
                    (click)="toggleAllTags()"
                    [attr.aria-pressed]="showAllTags()"
                    [title]="
                      showAllTags()
                        ? 'Showing all seat tags on this camera — click to show only this passenger'
                        : 'Showing only this passenger — click to show all seat tags on this camera'
                    "
                  >
                    <span nz-icon [nzType]="showAllTags() ? 'eye' : 'eye-invisible'" nzTheme="outline"></span>
                  </button>
                </div>
              }
            </header>

            <!-- Legacy v1 config banner -->
            @if (isLegacyTagConfig()) {
              <div class="alert alert-warning" style="margin-bottom: 12px;">
                <span nz-icon nzType="warning" nzTheme="outline"></span>
                <span>
                  This bus's audit tags need reconfiguring (legacy layout detected). Showing all 4 photos until tags are
                  rebuilt.
                </span>
              </div>
            }

            @if (entrySnapshots(selectedBusStopId()!).length > 0 || exitSnapshots(selectedBusStopId()!).length > 0) {
              <!-- ENTRY: the passenger's-channel image shown big, with a clickable strip of
                   every channel captured at this stop (green dot marks the passenger's camera).
                   Only the direction picked by the Entry/Exit toggle is shown. -->
              @if (shownDirection() === 'entry' && entrySnapshots(selectedBusStopId()!).length > 0) {
                <div class="snapshot-group">
                  <buspro-admin-seat-tagged-photo
                    class="snapshot-big"
                    direction="entry"
                    [photo]="entryBigPhoto(selectedBusStopId()!)"
                    [seatTags]="entryBigTags(selectedBusStopId()!)"
                    [activeSeatNumber]="activeSeatTag()?.seatNumber ?? null"
                  />
                  <div class="snapshot-thumbs">
                    @for (photo of entrySnapshots(selectedBusStopId()!); track photo.id) {
                      <button
                        type="button"
                        class="snapshot-thumb"
                        [class.snapshot-thumb--active]="photo.channel === entryBigPhoto(selectedBusStopId()!)?.channel"
                        [title]="'Channel ' + photo.channel"
                        (click)="selectEntryBig(photo.channel)"
                      >
                        <img [src]="photo.url" [alt]="'Channel ' + photo.channel" />
                        <span class="snapshot-thumb__ch">Ch {{ photo.channel }}</span>
                        @if (photo.channel === activeChannel()) {
                          <span class="snapshot-thumb__marker" title="This passenger's camera"></span>
                        }
                      </button>
                    }
                  </div>
                </div>
              }

              <!-- EXIT: same layout, shown when the toggle selects exit -->
              @if (shownDirection() === 'exit' && exitSnapshots(selectedBusStopId()!).length > 0) {
                <div class="snapshot-group">
                  <buspro-admin-seat-tagged-photo
                    class="snapshot-big"
                    direction="exit"
                    [photo]="exitBigPhoto(selectedBusStopId()!)"
                    [seatTags]="exitBigTags(selectedBusStopId()!)"
                    [activeSeatNumber]="activeSeatTag()?.seatNumber ?? null"
                  />
                  <div class="snapshot-thumbs">
                    @for (photo of exitSnapshots(selectedBusStopId()!); track photo.id) {
                      <button
                        type="button"
                        class="snapshot-thumb"
                        [class.snapshot-thumb--active]="photo.channel === exitBigPhoto(selectedBusStopId()!)?.channel"
                        [title]="'Channel ' + photo.channel"
                        (click)="selectExitBig(photo.channel)"
                      >
                        <img [src]="photo.url" [alt]="'Channel ' + photo.channel" />
                        <span class="snapshot-thumb__ch">Ch {{ photo.channel }}</span>
                        @if (photo.channel === activeChannel()) {
                          <span class="snapshot-thumb__marker" title="This passenger's camera"></span>
                        }
                      </button>
                    }
                  </div>
                </div>
              }
            } @else {
              <div class="state-container state-muted">
                <span nz-icon nzType="camera" nzTheme="outline"></span>
                <p>No photos available for this bus stop yet.</p>
                <small>Photos are captured automatically when the bus enters and exits geofences.</small>
              </div>
            }
          </div>

          <ng-template #promptSelectStop>
            <div class="state-container state-muted">
              <span nz-icon nzType="video-camera" nzTheme="outline"></span>
              <p>Select a bus stop to review MDVR snapshots.</p>
            </div>
          </ng-template>
        </section>

        <aside class="audit-sidebar audit-sidebar--right">
          <div class="info-panel">
            <header>
              <p class="panel-eyebrow">Booking Overview</p>
              <div class="panel-title">
                <h3>{{ activeSeatLabel() }}</h3>
                <span class="seat-class-tag" [ngClass]="getSeatClassBadge(seat.class)">{{ seatClassLabel(seat.class) }}</span>
              </div>

              @if (activePassenger(); as ap) {
                <div class="pax-row">
                  <span class="pax-name" [title]="ap.passenger.customerName || 'Passenger'">
                    {{ ap.passenger.customerName || 'Passenger' }}
                  </span>
                  <span class="audit-status-chip" [class.audit-status-chip--pending]="activeStatusChip().pending">
                    <i class="fa" [ngClass]="activeStatusChip().icon"></i>
                    <span>{{ activeStatusChip().label }}</span>
                  </span>
                </div>

                @if (activeSeatPassengers().length > 1) {
                  <div
                    class="leg-rail"
                    role="img"
                    [attr.aria-label]="'Leg ' + activeLegOrdinal() + ' of ' + activeSeatPassengers().length"
                  >
                    @for (leg of activeSeatPassengers(); track leg.passenger.id; let i = $index; let last = $last) {
                      <span
                        class="leg-rail__node"
                        [class.leg-rail__node--audited]="leg.passenger.audited"
                        [class.leg-rail__node--active]="leg.passenger.id === ap.passenger.id"
                        [title]="leg.passenger.customerName || 'Passenger'"
                      >
                        @if (leg.passenger.audited) {
                          <span nz-icon nzType="check" nzTheme="outline"></span>
                        } @else {
                          {{ i + 1 }}
                        }
                      </span>
                      @if (!last) {
                        <span class="leg-rail__bar" [class.leg-rail__bar--done]="leg.passenger.audited"></span>
                      }
                    }
                    <span class="leg-rail__label">leg {{ activeLegOrdinal() }} of {{ activeSeatPassengers().length }}</span>
                  </div>
                } @else {
                  <p class="pax-sub">Single passenger · full segment</p>
                }
              } @else if (activeSeatIsEmpty()) {
                <p class="panel-subtitle panel-subtitle--muted">Empty seat — no booking on this trip</p>
              }
            </header>

            <nz-divider class="compact-divider"></nz-divider>

            @if (activePassenger(); as passengerDetail) {
              <div
                class="route-summary"
                *ngIf="passengerDetail.originStop && passengerDetail.destinationStop; else routeFallback"
              >
                <div>
                  <label class="label">Origin</label>
                  <span class="value">{{ passengerDetail.originStop.name }}</span>
                </div>
                <div>
                  <label class="label">Destination</label>
                  <span class="value">{{ passengerDetail.destinationStop.name }}</span>
                </div>
              </div>

              <ng-template #routeFallback>
                <div class="route-summary">
                  <!-- Names what it is actually showing: the leg where the
                       passenger has one recorded, the bus's whole run only
                       where they do not. -->
                  <span class="label">{{ passengerDetail.passenger.subRouteName ? 'Journey' : 'Travel route' }}</span>
                  <span class="value">{{ passengerDetail.passenger.subRouteName || operationData()?.routeName }}</span>
                </div>
              </ng-template>

              <div class="audited-stops-readonly">
                <div class="audited-stop-row">
                  <label class="label">Audited Origin</label>
                  <span class="value" [class.value--changed]="auditedOriginChanged()">
                    {{ stopNameById(auditedOriginStopId()) }}
                  </span>
                </div>
                <div class="audited-stop-row">
                  <label class="label">Audited Drop-off</label>
                  <span class="value" [class.value--changed]="auditedDropChanged()">
                    {{ stopNameById(auditedDropStopId()) }}
                  </span>
                </div>
                <p class="hint">Use the Mark as Start / Mark as End menu on each bus stop above to set these.</p>
              </div>

              <div class="fare-summary" *ngIf="fareDifference() as fare">
                <div class="fare-row">
                  <span class="label">Original Fare</span>
                  <span class="value">{{ formatCurrency(fare.originalPrice) }}</span>
                </div>
                <div class="fare-row">
                  <span class="label">Audited Fare</span>
                  <span class="value">{{ formatCurrency(fare.auditedPrice) }}</span>
                </div>
                <div class="fare-row" [class.positive]="fare.difference > 0" [class.negative]="fare.difference < 0">
                  <span class="label">Difference</span>
                  <span class="value">{{ formatCurrency(fare.difference) }}</span>
                </div>
              </div>

              <div class="fare-summary" *ngIf="!fareDifference()">
                <span class="label">Fare difference will appear after selecting a drop-off stop.</span>
              </div>

              <div class="panel-actions">
                <button
                  class="btn btn-primary w-full flex items-center justify-center gap-2"
                  (click)="markPassengerAsAudited()"
                  [disabled]="!canSubmitPassengerAudit() || isSavingPassengerAudit"
                >
                  <span *ngIf="isSavingPassengerAudit" class="loading loading-spinner loading-sm"></span>
                  <span *ngIf="!isSavingPassengerAudit" nz-icon nzType="save"></span>
                  <span>{{ activePassenger()?.passenger?.audited ? 'Update' : 'Save' }}</span>
                </button>
                <p class="leg-hint" *ngIf="activeSeatPassengers().length > 1">
                  Switch legs from the passenger bar above (or <kbd>[</kbd> / <kbd>]</kbd>).
                </p>
              </div>
            } @else {
              <div class="empty-seat-panel">
                <p class="empty-seat-panel__lead">
                  Review the entry/exit photos for this seat. If someone is riding it without a booking, record them;
                  otherwise confirm the seat was empty.
                </p>

                @if (unbookedRidersForActiveSeat().length > 0) {
                  <div class="recorded-riders">
                    <p class="recorded-riders__title">Recorded unbooked riders</p>
                    @for (rider of unbookedRidersForActiveSeat(); track rider.id; let i = $index) {
                      <div class="rider-card">
                        <span class="rider-card__avatar" aria-hidden="true">
                          <span nz-icon nzType="user" nzTheme="outline"></span>
                        </span>
                        <div class="rider-card__body">
                          <div class="rider-card__top">
                            <span class="rider-card__name">Unbooked rider {{ i + 1 }}</span>
                            <span class="rider-card__fare">{{ formatCurrency(rider.evadedFare) }}</span>
                          </div>
                          <div class="rider-card__route">
                            {{ rider.auditedOriginStopName }} → {{ rider.auditedDropStopName }}
                          </div>
                          <div class="rider-card__actions">
                            <button type="button" class="rider-card__action" (click)="editRider(rider)">
                              <span nz-icon nzType="edit" nzTheme="outline"></span>
                              Edit
                            </button>
                            <button
                              type="button"
                              class="rider-card__action rider-card__action--danger"
                              (click)="removeUnbookedRider(rider)"
                            >
                              <span nz-icon nzType="delete" nzTheme="outline"></span>
                              Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    }
                    <p class="hint">Saved to this trip's audit record.</p>
                  </div>
                }

                @if (riderFormOpen()) {
                  <div class="rider-form">
                    <p class="rider-form__title">
                      {{ editingRiderId() ? 'Edit unbooked rider' : 'Unbooked rider' }} · {{ activeSeatLabel() }}
                    </p>
                    <p class="rider-form__lead">
                      Mark where they boarded and alighted — the evaded fare is computed from the route.
                    </p>

                    <div class="rider-modal__field">
                      <label>Boarded at</label>
                      <nz-select
                        class="w-full"
                        nzPlaceHolder="Select boarding stop"
                        [ngModel]="riderOriginStopId()"
                        (ngModelChange)="riderOriginStopId.set($event)"
                      >
                        <nz-option
                          *ngFor="let stop of riderBoardingOptions()"
                          [nzValue]="stop.id"
                          [nzLabel]="stop.name"
                        ></nz-option>
                      </nz-select>
                    </div>

                    <div class="rider-modal__field">
                      <label>Alighted at</label>
                      <nz-select
                        class="w-full"
                        nzPlaceHolder="Select alighting stop"
                        [ngModel]="riderDropStopId()"
                        (ngModelChange)="riderDropStopId.set($event)"
                      >
                        <nz-option
                          *ngFor="let stop of riderAlightingOptions()"
                          [nzValue]="stop.id"
                          [nzLabel]="stop.name"
                        ></nz-option>
                      </nz-select>
                    </div>

                    <div class="rider-modal__fare" *ngIf="riderEvadedFare() as est">
                      <span class="label">Evaded fare</span>
                      <span class="value">{{ formatCurrency(est.evadedFare) }}</span>
                    </div>

                    <div class="panel-actions">
                      <button
                        class="btn btn-warning w-full flex items-center justify-center gap-2"
                        [disabled]="!canRecordRider()"
                        (click)="recordUnbookedRider()"
                      >
                        <span nz-icon [nzType]="editingRiderId() ? 'save' : 'plus'"></span>
                        <span>{{ editingRiderId() ? 'Update rider' : 'Record rider' }}</span>
                      </button>
                      <button
                        type="button"
                        class="btn btn-ghost w-full flex items-center justify-center gap-2"
                        (click)="closeRiderForm()"
                      >
                        <span>Cancel</span>
                      </button>
                    </div>
                  </div>
                } @else {
                  <div class="panel-actions">
                    <button
                      class="btn btn-warning w-full flex items-center justify-center gap-2"
                      (click)="openRiderForm()"
                    >
                      <span nz-icon nzType="plus"></span>
                      <span>Add rider seen here</span>
                    </button>
                    @if (unbookedRidersForActiveSeat().length === 0) {
                      <button
                        class="btn btn-outline w-full flex items-center justify-center gap-2"
                        (click)="confirmEmptyActiveSeat()"
                        [disabled]="confirmedEmptyActiveSeat()"
                      >
                        <span nz-icon nzType="check"></span>
                        <span>{{ confirmedEmptyActiveSeat() ? 'Confirmed empty' : 'Confirm empty' }}</span>
                      </button>
                    } @else {
                      <p class="hint text-center">Seat accounted for by the recorded rider(s) above.</p>
                    }
                  </div>
                }
              </div>
            }

            <nz-divider class="compact-divider"></nz-divider>

            <div class="seat-nav">
              <button
                type="button"
                class="btn btn-outline btn-sm"
                title="Previous seat (←)"
                [disabled]="auditableSeats().length <= 1"
                (click)="navigateSeat('previous')"
              >
                <span nz-icon nzType="arrow-left"></span>
                Prev seat
              </button>
              <button
                type="button"
                class="btn btn-outline btn-sm"
                title="Next seat (→)"
                [disabled]="auditableSeats().length <= 1"
                (click)="navigateSeat('next')"
              >
                Next seat
                <span nz-icon nzType="arrow-right"></span>
              </button>
            </div>
          </div>

          <div class="audit-actions">
            <button
              *ngIf="allSeatsDone()"
              class="btn btn-primary w-full flex items-center justify-center gap-2"
              [disabled]="completeAuditMutation.isPending()"
              (click)="completeAudit()"
            >
              <span *ngIf="completeAuditMutation.isPending()" class="loading loading-spinner loading-sm"></span>
              <span *ngIf="!completeAuditMutation.isPending()" nz-icon nzType="check-circle"></span>
              <span>Complete Audit</span>
            </button>
          </div>
        </aside>
      </div>
    </ng-container>

    <ng-template #emptySeats>
      <div class="state-container state-muted">
        <span nz-icon nzType="dropbox"></span>
        <p>No seats are available to audit for this operation.</p>
        <button nz-button nzType="default" [routerLink]="['/audits/my-audits']">
          <span nz-icon nzType="arrow-left"></span>
          Back to My Audits
        </button>
      </div>
    </ng-template>
  </ng-container>
</div>

